import { FC, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Demo from "./Demo";
import { useShow } from "@/contexts/ShowProvider";
import ScrollParallaxHero from "./ScrollParallaxHero";
import TsParticles from "@/components/common/TsParticles";
import { ArrowRight, Sparkles, Check, ChevronDown } from "lucide-react";
import { useAuthForm } from "@/contexts/AuthFormProvider";
import { usePendingProject } from "@/contexts/PendingProjectProvider";

interface HeroProps {}

const Hero: FC<HeroProps> = () => {
  const { isShow } = useShow();
  const { showRegisterForm } = useAuthForm();
  const { setPendingProject } = usePendingProject();
  const [prompt, setPrompt] = useState("");
  const [showProjectNameModal, setShowProjectNameModal] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [showStackDropdown, setShowStackDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStackDropdown(false);
      }
    };

    if (showStackDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStackDropdown]);

  const handleStartBuilding = () => {
    if (!prompt.trim()) return;
    setShowProjectNameModal(true);
  };

  const handleProjectNameSubmit = () => {
    if (!projectName.trim()) return;

    // Store the pending project data
    setPendingProject({
      prompt: prompt.trim(),
      projectName: projectName.trim()
    });

    // Close modal
    setShowProjectNameModal(false);

    // Show register form
    window.lintrk('track', { conversion_id: 23344122 });
    showRegisterForm();
  };

  return (
    <div className="relative overflow-x-hidden w-full">
      {isShow ? (
        <Demo />
      ) : (
        <div className="relative overflow-x-hidden w-full">
          {/* High-Performance Particle Background */}
          <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
            <TsParticles
              key="hero-particles-stable" // Stable key to prevent re-mounting
              id="hero-particles"
              particleCount={30} // Reduced for better mobile performance
              speed={0.2} // Slower for better mobile performance
              linkDistance={100}
              opacity={0.5}
            />

            {/* Ambient Glow Effects */}
            <motion.div
              className="absolute top-20 left-10 sm:left-20 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl"
              style={{ willChange: 'transform, opacity' }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ duration: 8, repeat: Infinity, repeatType: "loop" }}
            />
            <motion.div
              className="absolute bottom-20 right-10 sm:right-20 w-60 sm:w-80 h-60 sm:h-80 bg-purple-500/5 rounded-full blur-3xl"
              style={{ willChange: 'transform, opacity' }}
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.6, 0.3, 0.6]
              }}
              transition={{ duration: 10, repeat: Infinity, repeatType: "loop" }}
            />
            <motion.div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] md:w-[1000px] h-[200px] sm:h-[350px] md:h-[500px] bg-gradient-to-r from-blue-500/3 via-purple-500/5 to-cyan-500/3 rounded-full blur-3xl"
              style={{ willChange: 'transform' }}
              animate={{
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear", repeatType: "loop" }}
            />
          </div>

          {/* Main Content */}
          <div className="relative z-10 w-full overflow-x-hidden">
            {/* Hero Title Section */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.5, delay: 0.3 }}
              className="text-center pt-32 sm:pt-36 md:pt-32 lg:pt-28 pb-8 sm:pb-12 px-4 sm:px-8 relative z-20 w-full"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 1.5, delay: 0.6 }}
                className="relative inline-block"
              >
                {/* Header Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 via-purple-500/20 to-cyan-500/15 rounded-3xl blur-3xl"></div>
                
                <h1 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight mb-4 sm:mb-6" style={{ fontFamily: 'GilroyMedium' }}>
                  <span className="text-white">The First AI for</span>
                  <br />
                  <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                    COMPLEX BACKEND
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                    DEVELOPMENT
                  </span>
                </h1>
                
                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 1 }}
                  className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto px-2"
                  style={{ fontFamily: 'GilroyMedium' }}
                >
                  Enterprise-grade AI that architects, codes, and maintains sophisticated backend systems with unprecedented precision
                </motion.p>
              </motion.div>
            </motion.div>

            {/* Prompt Input Section - Embedded directly in Hero */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.5, delay: 1.2 }}
              className="relative z-20 px-3 sm:px-6 md:px-8 pb-12 sm:pb-16"
            >
              <div className="max-w-4xl mx-auto">
                {/* Prompt Textarea */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 1.8 }}
                  className="relative group"
                >
                  {/* Subtle border glow */}
                  <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-xl sm:rounded-2xl opacity-50 group-hover:opacity-75 blur-sm transition-opacity duration-500"></div>

                  {/* Main container with dark theme */}
                  <div className="relative bg-[#0a0a0a] border border-white/10 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                    <div className="relative">
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Describe what you want to build... e.g., 'A cryptocurrency trading platform with order matching engine, wallet management, KYC verification, trading bots, and analytics'"
                        className="w-full min-h-[140px] sm:min-h-[160px] p-4 sm:p-5 md:p-6 bg-[#0f0f0f] text-white placeholder-gray-500 text-sm sm:text-base md:text-lg resize-none focus:outline-none focus:bg-[#141414] transition-all"
                        style={{
                          fontFamily: 'GilroyMedium',
                          lineHeight: '1.6'
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            handleStartBuilding();
                          }
                        }}
                      />
                    </div>

                    {/* Action Button Area with Stack Selector */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between p-3 sm:p-4 border-t border-white/10 bg-[#0a0a0a]">
                      {/* Tech Stack Selector - Left Side */}
                      <div className="relative order-2 sm:order-1" ref={dropdownRef}>
                        <button
                          type="button"
                          onClick={() => setShowStackDropdown(!showStackDropdown)}
                          className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 px-3 sm:px-4 py-2.5 sm:py-3 md:py-4 bg-[#141414] border border-white/10 rounded-lg sm:rounded-xl text-white hover:bg-[#1a1a1a] transition-all text-xs sm:text-sm md:text-base"
                        >
                          <span className="font-medium">Java, Spring Boot</span>
                          <div className="flex items-center gap-1">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                            <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-400 transition-transform ${showStackDropdown ? 'rotate-180' : ''}`} />
                          </div>
                        </button>

                        {/* Dropdown Menu */}
                        {showStackDropdown && (
                          <div className="absolute bottom-full left-0 mb-2 w-full sm:w-64 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50">
                            {/* Java Stack - Selected */}
                            <div className="flex items-center justify-between px-4 py-2.5 bg-green-500/10 border-l-2 border-green-500">
                              <span className="text-white font-medium text-sm">Java, Spring Boot</span>
                              <Check className="w-4 h-4 text-green-400" />
                            </div>

                            {/* Python Django - Coming Soon */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-white/5 cursor-not-allowed opacity-50">
                              <span className="text-white text-sm">Python, Django</span>
                              <span className="text-[10px] text-gray-400 bg-gray-700 px-2 py-0.5 rounded font-medium">Soon</span>
                            </div>

                            {/* Node.js - Coming Soon */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-white/5 cursor-not-allowed opacity-50">
                              <span className="text-white text-sm">JS, Node.js / Express</span>
                              <span className="text-[10px] text-gray-400 bg-gray-700 px-2 py-0.5 rounded font-medium">Soon</span>
                            </div>

                            {/* PHP Laravel - Coming Soon */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-white/5 cursor-not-allowed opacity-50">
                              <span className="text-white text-sm">PHP, Laravel</span>
                              <span className="text-[10px] text-gray-400 bg-gray-700 px-2 py-0.5 rounded font-medium">Soon</span>
                            </div>

                            {/* .NET Core - Coming Soon */}
                            <div className="flex items-center justify-between px-4 py-2.5 hover:bg-white/5 cursor-not-allowed opacity-50">
                              <span className="text-white text-sm">C#, .NET Core</span>
                              <span className="text-[10px] text-gray-400 bg-gray-700 px-2 py-0.5 rounded font-medium">Soon</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* CTA Button - Right Side on desktop, First on mobile */}
                      <motion.button
                        onClick={handleStartBuilding}
                        className="group relative order-1 sm:order-2 px-4 sm:px-6 md:px-8 py-3 sm:py-3 md:py-4 rounded-lg sm:rounded-xl bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 text-white font-bold text-sm sm:text-base md:text-lg shadow-xl overflow-hidden border border-blue-500/20"
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 20px 40px rgba(59, 130, 246, 0.4)"
                        }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          fontFamily: 'GilroyMedium',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          transform: 'translateZ(0)',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden'
                        }}
                      >
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                          animate={{ x: ['-100%', '200%'] }}
                          transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                        />

                        <motion.div
                          className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/30 via-purple-400/30 to-cyan-400/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          animate={{
                            scale: [1, 1.1, 1],
                            opacity: [0, 0.3, 0]
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                        />

                        <div className="relative flex items-center space-x-2 sm:space-x-3">
                          <span>Start Building Now</span>
                          <motion.div
                            whileHover={{ x: 3 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                          </motion.div>
                        </div>
                      </motion.button>
                    </div>
                  </div>

                  {/* Hint Text - Hidden on mobile */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 2 }}
                    className="hidden sm:block text-center text-xs sm:text-sm text-gray-400 mt-3"
                  >
                    <kbd className="px-2 py-1 bg-gray-800/50 rounded text-gray-300 text-xs">⌘</kbd> + <kbd className="px-2 py-1 bg-gray-800/50 rounded text-gray-300 text-xs">Enter</kbd> to submit
                  </motion.p>
                </motion.div>
              </div>
            </motion.div>

            {/* Scroll-Triggered Parallax Hero */}
            <ScrollParallaxHero />
          </div>
        </div>
      )}

      {/* Project Name Modal */}
      {showProjectNameModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[10001] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black border-2 border-white/10 rounded-3xl p-8 sm:p-10 max-w-lg w-full shadow-2xl"
          >
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500 rounded-3xl blur-2xl opacity-20"></div>

            {/* Content */}
            <div className="relative z-10">
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.1, type: "spring" }}
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-4"
                >
                  <Sparkles className="w-8 h-8 text-white" />
                </motion.div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'GilroyMedium' }}>
                  Name Your Project
                </h3>
                <p className="text-gray-400 text-sm sm:text-base">
                  Give your project a memorable name to get started
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g., Task Manager Pro"
                    className="w-full px-4 py-3 sm:py-4 bg-white/5 border-2 border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-sm sm:text-base"
                    style={{ fontFamily: 'GilroyMedium' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleProjectNameSubmit();
                      }
                    }}
                    autoFocus
                  />
                </div>

                <div className="flex gap-3 sm:gap-4">
                  <button
                    onClick={() => setShowProjectNameModal(false)}
                    className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl font-semibold transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleProjectNameSubmit}
                    disabled={!projectName.trim()}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Hero;